import { pipe } from 'fp-ts/function';
import { map } from 'fp-ts/lib/Either';
import * as t from 'io-ts';

import { Decoding, ErrorAsync, Http } from '@catawiki/runtime-toolkit';

import {
  collectionInfoRoute,
  expertRoute,
  getAuctionsRoute,
  getCategoriesRoute,
  getLotPricesRoute,
  getLotsRoute,
  getMenuCategoriesRoute,
  getPopularAuctionsRoute,
} from '../constants/apiRoutes';
import { Auction } from '../models/auction';
import { CollectionInfo, CollectionInfoResponse } from '../models/collection';
import { LotPrice } from '../models/lot';
import { setServerHeader } from './request';

export const AuctionsPayload = t.type({ auctions: t.array(Auction) });
export const PaginatedAuctionsPayload = t.type({
  auctions: t.array(Auction),
  meta: t.type({
    total: t.number,
    page: t.number,
    per_page: t.number,
  }),
});
export const PopularAuctionsPayload = t.type({
  auction_ids: t.array(t.number),
});

type SearchParamValue = string | number | true | number[] | undefined;

export type FetchAllAuctionsParams = Record<string, SearchParamValue> & {
  locale: string;
  category_ids?: number[];
  with_descendants?: true;
  status?: 'not_closed' | 'not_started';
  live_only?: true;
  sort?: 'close_at' | 'id_desc';
  page: number;
  per_page: number;
};

export async function fetchAuctions(
  locale: string,
  auctionIds: number[]
): ErrorAsync.PromiseEither<Auction[]> {
  return pipe(
    Http.get(getAuctionsRoute()),
    setServerHeader(),
    Http.setSearch({ locale, ids: auctionIds }, { arrayFormat: 'comma' }),
    Http.send<{ auctions: Auction[] }>()
  )
    .then(Decoding.decodeWith(AuctionsPayload))
    .then(map(({ auctions }) => auctions))
    .catch(ErrorAsync.errorToLeft);
}

export async function fetchPopularAuctions(
  locale: string,
  categoryId: number,
  categoryLevel: number
): ErrorAsync.PromiseEither<number[]> {
  return pipe(
    Http.get(getPopularAuctionsRoute()),
    setServerHeader(),
    Http.setSearch({
      locale,
      category_id: categoryId,
      category_level: categoryLevel,
    }),
    Http.send<{ auction_ids: number[] }>()
  )
    .then(Decoding.decodeWith(PopularAuctionsPayload))
    .then(map((payload) => payload.auction_ids))
    .catch(ErrorAsync.errorToLeft);
}

export async function fetchAllAuctions(
  params: FetchAllAuctionsParams
): ErrorAsync.PromiseEither<t.TypeOf<typeof PaginatedAuctionsPayload>> {
  return pipe(
    Http.get(getAuctionsRoute()),
    setServerHeader(),
    Http.setSearch(params, { arrayFormat: 'comma' }),
    Http.send()
  )
    .then(Decoding.decodeWith(PaginatedAuctionsPayload))
    .catch(ErrorAsync.errorToLeft);
}
export const RemoteLot = t.type({
  auction_id: t.number,
  id: t.number,
  original_image_url: t.string,
  title: t.string,
  url: t.string,
});

export type RemoteLot = t.TypeOf<typeof RemoteLot>;
export const LotsPayload = t.type({ lots: t.array(RemoteLot) });

export async function fetchLots(
  locale: string,
  lotIds: number[]
): ErrorAsync.PromiseEither<RemoteLot[]> {
  return pipe(
    Http.get(getLotsRoute()),
    setServerHeader(),
    Http.setSearch({ locale, ids: lotIds }, { arrayFormat: 'comma' }),
    Http.send<{ lots: RemoteLot[] }>()
  )
    .then(Decoding.decodeWith(LotsPayload))
    .then(map(({ lots }) => lots))
    .catch(ErrorAsync.errorToLeft);
}

export const LotPricesPayload = t.type({ lots: t.array(LotPrice) });

export async function fetchLotPrices(
  lotIds: number[]
): ErrorAsync.PromiseEither<LotPrice[]> {
  return pipe(
    Http.get(getLotPricesRoute()),
    setServerHeader(),
    Http.setSearch({ ids: lotIds }, { arrayFormat: 'comma' }),
    Http.send<{ lots: LotPrice[] }>()
  )
    .then(Decoding.decodeWith(LotPricesPayload))
    .then(map(({ lots }) => lots))
    .catch(ErrorAsync.errorToLeft);
}

export const RemoteCategory = t.type({
  url: t.string,
  id: t.number,
  name: t.string,
  image_url: t.string,
  color: t.string,
});

export type RemoteCategory = t.TypeOf<typeof RemoteCategory>;

export const RemoteCategoriesPayload = t.type({
  categories: t.array(RemoteCategory),
});
export async function fetchCategories(
  categoryIds: number[],
  locale = 'en'
): ErrorAsync.PromiseEither<RemoteCategory[]> {
  return pipe(
    Http.get(getCategoriesRoute()),
    setServerHeader(),
    Http.setSearch({ locale, ids: categoryIds }, { arrayFormat: 'comma' }),
    Http.send<{ categories: RemoteCategory[] }>()
  )
    .then(Decoding.decodeWith(RemoteCategoriesPayload))
    .then(map(({ categories }) => categories))
    .catch(ErrorAsync.errorToLeft);
}

export const RemoteMenuCategoriesPayload = t.type({
  menu: t.array(RemoteCategory),
});
export async function fetchMenuCategories(
  locale = 'en',
  levels = 1
): ErrorAsync.PromiseEither<RemoteCategory[]> {
  return pipe(
    Http.get(getMenuCategoriesRoute()),
    setServerHeader(),
    Http.setSearch({ locale, levels }, { arrayFormat: 'comma' }),
    Http.send<{ categories: RemoteCategory[] }>()
  )
    .then(Decoding.decodeWith(RemoteMenuCategoriesPayload))
    .then(map(({ menu }) => menu))
    .catch(ErrorAsync.errorToLeft);
}

export const RemoteExpert = t.type({
  id: t.number,
  name: t.string,
  expertise: t.string,
  gender: t.string,
  main_image: t.string,
  url: t.string,
});

export type RemoteExpert = t.TypeOf<typeof RemoteExpert>;
export const RemoteExpertsPayload = t.type({
  expert: RemoteExpert,
});
export async function fetchExpert(
  expertId: number,
  locale = 'en'
): ErrorAsync.PromiseEither<RemoteExpert> {
  const route = expertRoute(expertId);

  return pipe(
    Http.get(route),
    setServerHeader(),
    Http.setSearch({ locale }, { arrayFormat: 'comma' }),
    Http.send<{ expert: RemoteExpert }>()
  )
    .then(Decoding.decodeWith(RemoteExpertsPayload))
    .then(map(({ expert }) => expert))
    .catch(ErrorAsync.errorToLeft);
}

export async function fetchCollectionInfo(
  locale = 'en',
  collectionId: number
): ErrorAsync.PromiseEither<CollectionInfo> {
  const route = collectionInfoRoute(collectionId);
  return pipe(
    Http.get(route),
    setServerHeader(),
    Http.setSearch({ locale }, { arrayFormat: 'comma' }),
    Http.send()
  )
    .then(Decoding.decodeWith(CollectionInfoResponse))
    .then(map(({ collection }) => collection))
    .catch(ErrorAsync.errorToLeft);
}
